import { Heading4, P, TextLink } from '@ovotech/nebula';
import React, { PropsWithChildren } from 'react';
import { FootnoteProvider, PageWithNavigation } from '@/src/components';
import {
  Container,
  Reason,
  Reasons,
  StyledSubtitle,
  StyledTitle,
} from './Fallout.styled';
import { AnalyticsClick, useAccountContext } from '@ovotech/ui-tools';
import { SMART_BOOKING_URL } from '@/src/constants/endpoints';
import {
  EV_ANYTIME_SMART_CHARGER_LINK,
  HELP_WITH_YOUR_ACCOUNT,
  SWITCH_TO_OVO_ELECTRICITY,
} from '@/src/constants/links';
import { SkeletonCard } from '@/src/components/SkeletonLoading/SkeletonCard';
import {
  EV,
  parseIncompatibilityReasons,
  useEligibilityQuery,
} from '@monovo/ev-anytime';
import { SolarPanelsIncompatible } from '@/src/pages/AnytimeEligibilityV2/components/SolarPanelsIncompatible';
import { TechnicalGlitch } from '@/src/pages/AnytimeEligibilityV2/components/TechnicalGlitch';
import { PageHeader } from '@/src/pages/AnytimeEligibilityV2/components/PageHeader';
import { FalloutOther } from '@/src/pages/AnytimeEligibilityV2/pages/Fallout/FalloutOther';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { FalloutChargeAnytime } from '@/src/pages/AnytimeEligibilityV2/pages/Fallout/FalloutChargeAnytime';

const Loading = () => {
  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <PageHeader
        title="Check your set-up"
        showDescription={false}
        testId="check-eligibility-header"
      />
      <SkeletonCard data-testid="loading-screen" />
    </PageWithNavigation>
  );
};

const ChooseYourCharger = () => {
  return (
    <AnalyticsClick
      name={
        EV.ELIGIBILITY
          .EVS_OVO_CHARGE_ANYTIME_OUTCOME_INELIGIBLE_GET_SMART_CHARGER_CLICKED
      }
    >
      <TextLink
        data-event-name={
          'Click-EVS - EVS OVO Drive Anytime Smart charger offer button clicked [Orion Web]'
        }
        href={EV_ANYTIME_SMART_CHARGER_LINK}
        opensInNewWindow
      >
        Choose your EV charger today
      </TextLink>
    </AnalyticsClick>
  );
};

const PageWrapper = ({
  children,
  title,
}: PropsWithChildren<{ title?: string }>) => {
  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <PageHeader
        title={title ?? 'Check your set-up'}
        showDescription={false}
        testId="check-eligibility-header"
      />
      <Container>
        <Reasons>{children}</Reasons>
      </Container>
    </PageWithNavigation>
  );
};

export const Fallout = () => {
  const {
    payAsYouDriveBolton,
    payAsYouDriveMonthlyBolton,
    isLoading: isCABoltonLoading,
  } = useChargeAnytimeBoltonState();
  const { accountId } = useAccountContext();
  const {
    data: eligibility,
    isFetching: isFetchingEligibility,
    isError: isEligibilityQueryError,
  } = useEligibilityQuery();
  const { reasons, isOnlyInvalidOther, numberOfSupportedReasons } =
    parseIncompatibilityReasons(
      eligibility?.compatibilityStatus === 'INCOMPATIBLE'
        ? eligibility.reasonsForIncompatibility
        : undefined,
    );

  if (isCABoltonLoading || isFetchingEligibility) {
    return <Loading />;
  }

  if (isEligibilityQueryError) {
    return (
      <PageWrapper>
        <TechnicalGlitch />
      </PageWrapper>
    );
  }

  if (payAsYouDriveBolton || payAsYouDriveMonthlyBolton) {
    return (
      <PageWrapper title={'You already have Charge Anytime'}>
        <FalloutChargeAnytime />
      </PageWrapper>
    );
  }

  const renderFallout = () => {
    const smartMeterBookingRoute = `${SMART_BOOKING_URL}?accountId=${accountId}&utm_source=anytimeEligibility&utm_medium=MyOVO&utm_campaign=smart-meter-booking`;

    if (isOnlyInvalidOther) {
      return (
        <PageWrapper>
          <FalloutOther />
        </PageWrapper>
      );
    }

    return (
      <PageWrapper>
        <Reason>
          <StyledTitle>Your set-up isn’t compatible right now.</StyledTitle>
          <StyledSubtitle>
            {numberOfSupportedReasons > 1
              ? `Here are ${numberOfSupportedReasons} things you can do to get Charge Anytime`
              : 'Here’s what you can do to get Charge Anytime'}
          </StyledSubtitle>
        </Reason>
        {reasons.includes('NotLiable') && (
          <Reason data-testid="not-liable">
            <Heading4>
              Your name will need to be added to the account's bills
            </Heading4>
            <P>
              You'll need to be made &apos;Financially Liable&apos; on the
              account by adding your name to the bills - you can find out more{' '}
              <TextLink href={HELP_WITH_YOUR_ACCOUNT} opensInNewWindow>
                here
              </TextLink>
            </P>
          </Reason>
        )}
        {reasons.includes('NoElectricityTariff') && (
          <Reason data-testid={'no-electricity-tariff'}>
            <Heading4>Get electricity with OVO</Heading4>
            <P>
              At the moment you’re on a gas-only plan. Switch to OVO for your
              electricity and add Charge Anytime to your plan for free.
            </P>
            <AnalyticsClick
              name={
                EV.ELIGIBILITY
                  .EVS_OVO_CHARGE_ANYTIME_OUTCOME_INELIGIBLE_GET_ELECTRICITY_CLICKED
              }
            >
              <TextLink
                data-event-name={
                  'EVS - EVS OVO Drive Anytime Electricity offer button clicked'
                }
                href={SWITCH_TO_OVO_ELECTRICITY}
                opensInNewWindow
              >
                Get an electricity quote
              </TextLink>
            </AnalyticsClick>
          </Reason>
        )}
        {reasons.includes('NoSmartMeter') && (
          <Reason data-testid="smart-meter-offer">
            <Heading4>Save money with a smart meter</Heading4>
            <P>
              Charge Anytime needs a smart meter that sends half-hourly readings
              to connect your EV or charger to your OVO account, and the grid.
              If you don’t already have an electricity smart meter, we’ll
              install one for free.
            </P>
            <AnalyticsClick
              name={
                EV.ELIGIBILITY
                  .EVS_OVO_CHARGE_ANYTIME_OUTCOME_INELIGIBLE_GET_SMART_METER_CLICKED
              }
            >
              <TextLink
                data-event-name={
                  'EVS - EVS OVO Charge Anytime D2V Outcome Ineligible Get Smart Meter clicked'
                }
                opensInNewWindow
                href={smartMeterBookingRoute}
              >
                Get a smart meter
              </TextLink>
            </AnalyticsClick>
          </Reason>
        )}
        {(reasons.includes('IncompatibleVehicle') ||
          reasons.includes('IncompatibleCharger')) && (
          <Reason data-testid="incompatible-car">
            <Heading4>Connect through a smart EV charger</Heading4>
            <P>
              Your EV isn’t compatible right now, but you could still get Charge
              Anytime through a compatible smart EV charger – and we can install
              it for you.
            </P>
            <ChooseYourCharger />
          </Reason>
        )}
        {reasons.includes('IncompatibleWithSolar') && (
          <Reason data-testid="has-solar-panels">
            <SolarPanelsIncompatible />
          </Reason>
        )}
        {reasons.includes('NoInternetAccess') && (
          <Reason data-testid="has-no-internet-access">
            <Heading4>
              Make sure your EV connects to the internet while smart charging
            </Heading4>
            <P>
              If your EV has an internet connection, make sure you park where
              you can get a clear signal.
            </P>
            <P>
              If your EV doesn’t have an internet connection, you could still
              get Charge Anytime through one of our compatible smart EV chargers
              – and we’ll install it too.
            </P>
            <ChooseYourCharger />
          </Reason>
        )}
      </PageWrapper>
    );
  };

  return (
    <FootnoteProvider markerType="number">
      <div>{renderFallout()}</div>
    </FootnoteProvider>
  );
};
